/**
 * Ingreso — tres vías, según quién seas:
 *   1. Código de acceso rotativo (admin al instante, sin email).
 *   2. Email + contraseña (solo administradores).
 *   3. Email + código de 6 dígitos (operadores, caja, apostadores).
 *
 * La pestaña activa queda guardada: la ventanilla siempre abre en la que usa el operador.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth.js';
import { getSetting, setSetting } from '../lib/db.js';
import { useT, useErrorText } from '../lib/i18n.js';
import { ApiProblem, OfflineError } from '../lib/api.js';
import { Button } from '../components/ui.js';

type Tab = 'code' | 'password' | 'otp';

const TABS: readonly Tab[] = ['code', 'password', 'otp'];

export function Login(): JSX.Element {
  const t = useT();
  const errorText = useErrorText();
  const { requestOtp, verifyOtp, loginWithPassword, loginWithAccessCode, status } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState<Tab>('code');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<'enter' | 'code'>('enter');
  const [devCode, setDevCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Recupera la última pestaña usada en este dispositivo.
  useEffect(() => {
    void (async () => {
      const saved = await getSetting('login.tab');
      if (saved && (TABS as readonly string[]).includes(saved)) setTab(saved as Tab);
    })();
  }, []);

  useEffect(() => {
    void setSetting('login.tab', tab);
  }, [tab]);

  useEffect(() => {
    if (status === 'authenticated') navigate('/pelea', { replace: true });
  }, [status, navigate]);

  const fail = (e: unknown): void => {
    if (e instanceof OfflineError) {
      setError(t('error.network'));
      return;
    }
    setError(errorText((e as ApiProblem).code ?? 'INTERNAL', (e as Error).message));
  };

  const onCode = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await loginWithAccessCode(code.trim());
      navigate('/pelea', { replace: true });
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const onPassword = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await loginWithPassword(email, password);
      navigate('/pelea', { replace: true });
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const onRequestOtp = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await requestOtp(email.trim().toLowerCase());
      setDevCode(res.devCode ?? null);
      setStep('code');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const onVerify = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await verifyOtp({ email: email.trim().toLowerCase(), code: otp.trim() });
      navigate('/pelea', { replace: true });
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pit-arena relative flex min-h-full items-center justify-center p-4">
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="pit-grain absolute inset-0 opacity-[0.18]" />
        <div className="pit-anim-breathe absolute left-1/4 top-1/3 h-80 w-80 rounded-[var(--pit-radius-full)] bg-[var(--pit-accent-red)] opacity-[0.10] blur-3xl" />
        <div className="pit-anim-breathe absolute bottom-1/4 right-1/4 h-80 w-80 rounded-[var(--pit-radius-full)] bg-[var(--pit-accent-cyan)] opacity-[0.10] blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <span
            aria-hidden
            className="pit-hex pit-anim-float grid h-16 w-16 place-items-center"
            style={{
              background:
                'linear-gradient(160deg, color-mix(in srgb, var(--pit-accent-red) 62%, transparent), color-mix(in srgb, var(--pit-accent-amber) 22%, transparent))',
              boxShadow: '0 0 44px -10px var(--pit-accent-red)',
            }}
          >
            <span className="pit-mono text-[var(--pit-text-xl)] font-[var(--pit-weight-black)] text-white">TP</span>
          </span>
          <p className="pit-title pit-mono text-[var(--pit-text-3xl)] font-[var(--pit-weight-black)] uppercase leading-[var(--pit-leading-none)] tracking-[var(--pit-tracking-widest)]">
            {t('app.name')}
          </p>
          <p className="text-[var(--pit-text-sm)] text-[var(--pit-text-secondary)]">
            {t('app.tagline')}
          </p>
        </div>

        <div
          role="tablist"
          aria-label={t('auth.quick')}
          className="mb-4 grid grid-cols-3 gap-1 rounded-[var(--pit-radius-lg)] border border-[var(--pit-border-subtle)] bg-[var(--pit-bg-sunken)] p-1"
        >
          {TABS.map((item) => {
            const active = tab === item;
            const labelKey =
              item === 'code' ? 'auth.tabCode' : item === 'password' ? 'auth.tabPassword' : 'auth.tabOtp';
            return (
              <button
                key={item}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => {
                  setTab(item);
                  setError(null);
                  setStep('enter');
                }}
                className={[
                  'pit-touch rounded-[var(--pit-radius-md)] px-2 py-2',
                  'text-[var(--pit-text-2xs)] font-[var(--pit-weight-semibold)] uppercase tracking-[var(--pit-tracking-wider)]',
                  'transition-colors duration-[var(--pit-duration-fast)]',
                  active
                    ? 'bg-[var(--pit-bg-overlay)] text-[var(--pit-text-primary)]'
                    : 'text-[var(--pit-text-muted)] hover:text-[var(--pit-text-secondary)]',
                ].join(' ')}
              >
                {t(labelKey)}
              </button>
            );
          })}
        </div>

        <div className="pit-glass pit-scanlines relative overflow-hidden rounded-[var(--pit-radius-xl)] p-5">
          <span
            aria-hidden
            className="absolute inset-x-0 top-0 h-1"
            style={{
              background:
                'linear-gradient(90deg, var(--pit-corner-red), var(--pit-accent-amber), var(--pit-accent-cyan))',
            }}
          />
          {tab === 'code' && (
            <form onSubmit={(e) => void onCode(e)} className="flex flex-col gap-4">
              <div>
                <label
                  htmlFor="code"
                  className="pit-mono block text-[var(--pit-text-2xs)] uppercase tracking-[var(--pit-tracking-wider)] text-[var(--pit-text-muted)]"
                >
                  {t('auth.codeLabel')}
                </label>
                <input
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
                  placeholder={t('auth.codePlaceholder')}
                  maxLength={20}
                  autoComplete="off"
                  spellCheck={false}
                  className="pit-mono pit-touch mt-2 w-full rounded-[var(--pit-radius-md)] border border-[var(--pit-border-subtle)] bg-[var(--pit-bg-sunken)] px-3 py-3 text-center text-[var(--pit-text-lg)] tracking-[var(--pit-tracking-wide)]"
                />
                <p className="mt-2 text-[var(--pit-text-xs)] text-[var(--pit-text-muted)]">
                  {t('auth.codeHint')}
                </p>
              </div>
              <Button type="submit" block loading={busy} disabled={code.trim().length < 8}>
                {t('auth.enter')}
              </Button>
            </form>
          )}

          {tab === 'password' && (
            <form onSubmit={(e) => void onPassword(e)} className="flex flex-col gap-4">
              <div>
                <label
                  htmlFor="email"
                  className="pit-mono block text-[var(--pit-text-2xs)] uppercase tracking-[var(--pit-tracking-wider)] text-[var(--pit-text-muted)]"
                >
                  {t('auth.emailLabel')}
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  className="pit-touch mt-2 w-full rounded-[var(--pit-radius-md)] border border-[var(--pit-border-subtle)] bg-[var(--pit-bg-sunken)] px-3 py-3"
                />
              </div>
              <div>
                <label
                  htmlFor="password"
                  className="pit-mono block text-[var(--pit-text-2xs)] uppercase tracking-[var(--pit-tracking-wider)] text-[var(--pit-text-muted)]"
                >
                  {t('auth.passwordLabel')}
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.passwordPlaceholder')}
                  className="pit-touch mt-2 w-full rounded-[var(--pit-radius-md)] border border-[var(--pit-border-subtle)] bg-[var(--pit-bg-sunken)] px-3 py-3"
                />
              </div>
              <Button type="submit" block loading={busy}>
                {t('auth.enter')}
              </Button>
            </form>
          )}

          {tab === 'otp' &&
            (step === 'enter' ? (
              <form onSubmit={(e) => void onRequestOtp(e)} className="flex flex-col gap-4">
                <div>
                  <label
                    htmlFor="otp-email"
                    className="pit-mono block text-[var(--pit-text-2xs)] uppercase tracking-[var(--pit-tracking-wider)] text-[var(--pit-text-muted)]"
                  >
                    {t('auth.emailLabel')}
                  </label>
                  <input
                    id="otp-email"
                    type="email"
                    required
                    autoComplete="email"
                    inputMode="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('auth.emailPlaceholder')}
                    className="pit-touch mt-2 w-full rounded-[var(--pit-radius-md)] border border-[var(--pit-border-subtle)] bg-[var(--pit-bg-sunken)] px-3 py-3"
                  />
                </div>
                <Button type="submit" block loading={busy}>
                  {t('auth.sendCode')}
                </Button>
              </form>
            ) : (
              <form onSubmit={(e) => void onVerify(e)} className="flex flex-col gap-4">
                <p className="text-[var(--pit-text-sm)] text-[var(--pit-text-secondary)]">
                  {t('auth.sentTo', { email })}
                </p>
                {devCode && (
                  <p className="pit-mono rounded-[var(--pit-radius-md)] border border-[var(--pit-accent-amber)] px-3 py-2 text-center text-[var(--pit-text-sm)] text-[var(--pit-accent-amber)]">
                    {t('auth.devCode', { code: devCode })}
                  </p>
                )}
                <input
                  id="otp-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d{6}"
                  maxLength={6}
                  required
                  autoFocus
                  aria-label={t('auth.codeLabel')}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  className="pit-mono pit-touch w-full rounded-[var(--pit-radius-md)] border border-[var(--pit-border-subtle)] bg-[var(--pit-bg-sunken)] px-3 py-4 text-center text-[var(--pit-text-3xl)] tracking-[var(--pit-tracking-widest)]"
                />
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => setStep('enter')}>
                    {t('app.cancel')}
                  </Button>
                  <Button type="submit" block loading={busy}>
                    {t('auth.verify')}
                  </Button>
                </div>
              </form>
            ))}

          {error && (
            <p
              role="alert"
              className="mt-4 rounded-[var(--pit-radius-sm)] border border-[var(--pit-state-danger)] px-3 py-2 text-[var(--pit-text-sm)] text-[var(--pit-state-danger)]"
            >
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
